import type { SessionPlayback } from "./types"

/**
 * The parts of a graph `Video` that playback needs. Studies and digital events
 * reach it through unrelated queries whose generated types are not assignable to
 * each other, so this is described structurally and each projection passes its
 * own `video` straight in.
 */
interface VideoLike {
  readonly progress?: { readonly playheadSeconds: number } | null
  readonly vendorIdentifiers?: {
    readonly brightcove?: { readonly accountId?: string | null; readonly videoCloudId?: string | null } | null
  } | null
  readonly videoSources?: {
    readonly __typename?: string
    readonly durationMilliseconds?: number | null
    readonly thumbnailsUrl?: string | null
  } | null
}

const playableSources = (
  video: VideoLike,
): { durationMilliseconds?: number | null; thumbnailsUrl?: string | null } | undefined =>
  video.videoSources?.__typename === "VideoSources" ? video.videoSources : undefined

/**
 * Builds the player's view of a session, or `undefined` when it cannot be played.
 *
 * Two things make a session unplayable and both are ordinary on INT: the account
 * has no entitlement, so `videoSources` answers `NoAccessError` and carries no
 * duration; or the video predates Brightcove ingest and has no `videoCloudId`.
 * Returning `undefined` lets the rail keep drawing the card while the player
 * refuses to start, which is what the TV app does.
 */
export const toPlayback = (video: VideoLike | null | undefined): SessionPlayback | undefined => {
  if (!video) {
    return undefined
  }

  const brightcove = video.vendorIdentifiers?.brightcove
  const accountId = brightcove?.accountId?.trim()
  const videoCloudId = brightcove?.videoCloudId?.trim()

  if (!accountId || !videoCloudId) {
    return undefined
  }

  const sources = playableSources(video)
  const durationMilliseconds = sources?.durationMilliseconds

  return {
    accountId,
    videoCloudId,
    durationSeconds: durationMilliseconds && durationMilliseconds > 0 ? durationMilliseconds / 1000 : undefined,
    playheadSeconds: video.progress?.playheadSeconds ?? 0,
    thumbnailsUrl: sources?.thumbnailsUrl ?? undefined,
  }
}
